import type { Locale, LocaleStrings } from "../ai_literacy/conceptCopy.js";

export { type Locale, type LocaleStrings, type StemTemplate } from "../ai_literacy/conceptCopy.js";

export const DEFAULT_LOCALE: Locale = "en";

/** Exam Language 2 is English; generated stems stay English if the shell locale changes. */
export const SUPPORTED_LOCALES: readonly Locale[] = ["en"];

const EN: LocaleStrings = {
  titles: {
    enterprise_ai_architecture: "Enterprise AI system design",
    model_selection_eval: "Model selection and evaluation",
    data_pipelines_quality: "Data pipelines and quality",
    mlops_lifecycle: "MLOps and the production lifecycle",
    generative_rag: "Generative models and RAG",
    agents_mcp: "Agentic systems and MCP",
    act_scope_roles: "AI Act: scope and operator roles",
    act_prohibited: "Prohibited AI practices",
    act_high_risk: "High-risk classification",
    act_obligations_ops: "Provider and deployer obligations",
    gpai_transparency: "GPAI and transparency",
    trustworthiness_eval: "Trustworthiness, eval, and data protection",
    ai_office_enforcement: "AI Office and market surveillance",
    policy_watch_impact: "Policy, watch, and enforcement soundness",
  },
  blurbs: {
    enterprise_ai_architecture:
      "Build versus buy, batch versus online serving, APIs and interoperability, and hybrid or cloud placement for an end-to-end AI system — design patterns, not a framework title.",
    model_selection_eval:
      "Choosing a model for the task, reading metrics that can lie, train/validate/test leakage, and overfitting versus underfitting.",
    data_pipelines_quality:
      "Preparation, lineage, quality contracts, train/serving skew, and who owns security and governance of the data that feeds production models.",
    mlops_lifecycle:
      "Deploy, monitor, retrain, and roll back: data versus concept drift, what monitors actually catch, and QA after go-live.",
    generative_rag:
      "When retrieval-augmented generation beats fine-tuning or a bigger window; chunking and retrieval failure; hallucination; prompt injection and context leakage.",
    agents_mcp:
      "Tool-using loops, human-in-the-loop gates, permissions, and MCP as a context and tool protocol — not the foundation model itself.",
    act_scope_roles:
      "Article 3 definitions of AI system, provider, deployer, importer, distributor and authorised representative; Article 2 exclusions; Article 25 role flip.",
    act_prohibited:
      "Article 5 bans, including the 2026 intimate-image and CSAM additions, and the split between provider placing and deployer use.",
    act_high_risk:
      "Article 6 dual track (Annex I products versus Annex III uses), the 6(3) derogation and profiling override, and the delayed application dates after the Digital Omnibus.",
    act_obligations_ops:
      "Article 16 versus 26, logging and human oversight, FRIA, literacy as support not a guaranteed level, quality management and registration.",
    gpai_transparency:
      "GPAI model versus system, the 10^25 FLOP systemic-risk presumption, Article 53 and 55 duties, and Article 50 marking and disclosure.",
    trustworthiness_eval:
      "Fairness, explainability, traceability and reproducibility of decision outputs; eval versus red-teaming; DPIA beside FRIA; Article 4a special-category processing.",
    ai_office_enforcement:
      "AI Office, Board, national market surveillance, EDPS for Union institutions, Article 75 exclusive competence, notified bodies, and Article 99 fine bands.",
    policy_watch_impact:
      "What the Act is for (internal market, trustworthy AI, rights, innovation), technology watch, standardisation as a conformity path, and technically sound enforcement.",
  },
  stems: [
    (b) => `Which concept best matches this description?\n\n${b}`,
    (b) => `An institution SME wrote this note. Which topic is it?\n\n${b}`,
    (b) => `Pick the correct heading for this summary:\n\n${b}`,
    (b) => `From the Field 1 map, this belongs to which concept?\n\n${b}`,
    (b) => `Match the blurb to the right concept title:\n\n${b}`,
    (b) => `Which study node is this describing?\n\n${b}`,
  ],
};

const FALLBACK: LocaleStrings = {
  titles: { ...EN.titles },
  blurbs: { ...EN.blurbs },
  stems: [...EN.stems],
};

export const COPY: Readonly<Record<Locale, LocaleStrings>> = {
  en: EN,
  es: FALLBACK,
  el: FALLBACK,
};

export function getCopy(locale: Locale = "en"): LocaleStrings {
  return COPY[locale] ?? EN;
}

export const CONCEPT_TITLES: Readonly<Record<string, string>> = EN.titles;
export const CONCEPT_BLURBS: Readonly<Record<string, string>> = EN.blurbs;
